import { useState, useEffect, useContext } from 'react';
import { MemberContext } from '../components/context';
import { combineValuesWithSepartors, getValuesFromDataObject } from '../utils/util';

const getVal = require('lodash/get');

const hasOptionValue = (value) => value !== undefined && value !== null && value !== '';

const escapeRegExp = (value) => value.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&');

const getDynamicPrefillData = ({ dynamicPrefilledWith, filledFormData, dataObject }) => {
  if (dynamicPrefilledWith === 'formSubmissionPayload') {
    return getVal(dataObject, 'formSubmissionPayload') || filledFormData;
  }

  if (dynamicPrefilledWith === 'filledFormData') {
    return filledFormData;
  }

  return null;
};

const getIndexedOptionIndices = (prefillData, indexFromTemplate) => {
  const optionKeyPattern = new RegExp(
    `^${escapeRegExp(indexFromTemplate).replace('\\{index\\}', '(\\d+)')}$`
  );

  return Object.keys(prefillData)
    .reduce((indices, key) => {
      const matchedIndex = key.match(optionKeyPattern);
      if (matchedIndex && hasOptionValue(prefillData[key])) {
        indices.push(Number(matchedIndex[1]));
      }
      return indices;
    }, [])
    .sort((firstIndex, secondIndex) => firstIndex - secondIndex);
};

const getArrayFromCandidatePaths = (prefillData, candidatePaths) => {
  const matchedPath = candidatePaths.find((candidatePath) => {
    const dataArr = getVal(prefillData, candidatePath);
    return Array.isArray(dataArr) && dataArr.length > 0;
  });

  return matchedPath ? getVal(prefillData, matchedPath) : [];
};

const getPersonFirstName = (person) => getVal(person, 'names.0.first')
  || getVal(person, 'personalInformation.names.0.first')
  || getVal(person, 'firstName')
  || getVal(person, 'first')
  || '';

const getTemplateValue = (template, index) => {
  if (typeof template !== 'string') {
    return '';
  }

  return template.replaceAll('{index}', String(index));
};

const buildPrefillLabel = (record, option = {}) => {
  const { labelPaths, labelPath, labelJoin = ', ' } = option;
  let resolvedLabelPaths = [];

  if (Array.isArray(labelPaths) && labelPaths.length > 0) {
    resolvedLabelPaths = labelPaths;
  } else if (typeof labelPath === 'string' && labelPath) {
    resolvedLabelPaths = [labelPath];
  }

  if (resolvedLabelPaths.length === 0) {
    return '';
  }

  const labelValues = resolvedLabelPaths.reduce((accumulator, path) => {
    const value = getVal(record, path);
    if (hasOptionValue(value)) {
      accumulator.push(value);
    }
    return accumulator;
  }, []);

  return labelValues.length > 0 ? labelValues.join(labelJoin) : '';
};

const getPrefillSourceArray = (prefillData, sourceArrayPaths) => {
  if (!Array.isArray(sourceArrayPaths) || sourceArrayPaths.length === 0) {
    return [];
  }

  const matchedPath = sourceArrayPaths.find((sourceArrayPath) => {
    const sourceData = getVal(prefillData, sourceArrayPath);
    return Array.isArray(sourceData) && sourceData.length > 0;
  });

  return matchedPath ? getVal(prefillData, matchedPath) : [];
};

const getPrefillArrayOptions = (prefillData, option = {}) => {
  const sourceRecords = getPrefillSourceArray(prefillData, option.sourceArrayPaths);
  if (sourceRecords.length === 0) {
    return [];
  }

  return sourceRecords.reduce((accumulator, record, index) => {
    const position = index + 1;
    const label = buildPrefillLabel(record, option);

    if (!hasOptionValue(label)) {
      return accumulator;
    }

    accumulator.push({
      id: option.idTemplate ? getTemplateValue(option.idTemplate, position) : `option-${position}`,
      value: option.valueTemplate ? getTemplateValue(option.valueTemplate, position) : label,
      label,
    });

    return accumulator;
  }, []);
};

const getFallbackIndexedValues = (prefillData, option = {}) => {
  const candidatePaths = option.fallbackArrayPaths || [];

  if (!Array.isArray(candidatePaths) || candidatePaths.length === 0) {
    return [];
  }

  const personRecords = getArrayFromCandidatePaths(
    prefillData,
    candidatePaths
  );

  return personRecords.reduce((acc, person, index) => {
    const firstName = getPersonFirstName(person);
    if (hasOptionValue(firstName)) {
      acc.push({ index: index + 1, value: firstName });
    }
    return acc;
  }, []);
};

const useDropDownOptions = (props) => {
  const {
    fetchUrl,
    isFieldOptionsToBeLookedUp,
    formContainer,
    fieldOptionsToBeLookedUpFrom,
    isDynamicPrefilledOptions,
    dynamicPrefilledWith,
    filledFormData,
    fieldOptions,
    fromStaticMetadataWithDynamicKey,
    fieldId,
    dataObject,
    fieldOptionObject = {
      type: 'default',
    },
    dynamicEntityDataOptions,
    entityData = {},
    entityToEdit,
  } = props;

  const [options, setOptions] = useState([]);
  const memberContextVal = useContext(MemberContext);
  const extendedDataObject = { ...dataObject, memberContext: memberContextVal };
  const dynamicPrefillData = getDynamicPrefillData({
    dynamicPrefilledWith,
    filledFormData,
    dataObject,
  });
  const createOption = (label, value, accountToken = '') => ({ label, value, accountToken });

  /* eslint complexity: ["error", 30] -- need to increase complexity */
  const dependentValues = fieldOptionObject?.dependsOn?.reduce((deps, key) => {
    deps.push(getVal(dataObject, key));
    return deps;
  }, []) || [];

  const effectDependencies = [...dependentValues];

  if (isDynamicPrefilledOptions && dynamicPrefillData) {
    effectDependencies.push(dynamicPrefillData, fieldOptions);
  }

  if (dynamicEntityDataOptions) {
    effectDependencies.push(entityData, entityToEdit?.compositeKey, fieldOptions);
  }

  useEffect(
    () => {
      if (fieldOptionObject.type === 'default') {
        if (fetchUrl) {
          setOptions(memberContextVal);
        } else if (isFieldOptionsToBeLookedUp) {
          if (
            formContainer
            && formContainer.operations
            && formContainer.operations[fieldOptionsToBeLookedUpFrom]
          ) {
            setOptions(formContainer.operations[fieldOptionsToBeLookedUpFrom]);
          }
        } else if (isDynamicPrefilledOptions && dynamicPrefillData) {
          const dynamicFieldOptions = [];
          if (dynamicPrefillData) {
            fieldOptions.forEach((option) => {
              if (option.indexFrom && option.indexFrom.includes('{index}')) {
                const matchedIndices = getIndexedOptionIndices(
                  dynamicPrefillData,
                  option.indexFrom
                );

                if (matchedIndices.length === 0) {
                  const fallbackValues = getFallbackIndexedValues(
                    dynamicPrefillData,
                    option
                  );

                  fallbackValues.forEach((record) => {
                    const expandedOption = {
                      id: option.id ? option.id.replace('{index}', record.index) : `option-${record.index}`,
                      value: option.value
                        ? option.value.replace('{index}', record.index)
                        : String(record.index),
                      label: record.value,
                    };
                    dynamicFieldOptions.push(expandedOption);
                  });
                }

                matchedIndices.forEach((idx) => {
                  const expandedOption = {
                    id: option.id ? option.id.replace('{index}', idx) : `option-${idx}`,
                    value: option.value ? option.value.replace('{index}', idx) : String(idx),
                  };

                  if (Array.isArray(option.label)) {
                    const labelStrArr = [];
                    option.label.forEach((dynamicFieldVal) => {
                      const expandedKey = dynamicFieldVal.replace('{index}', idx);
                      if (hasOptionValue(dynamicPrefillData[expandedKey])) {
                        labelStrArr.push(dynamicPrefillData[expandedKey]);
                      }
                    });

                    if (labelStrArr.length > 0) {
                      expandedOption.label = labelStrArr.join(', ');
                      dynamicFieldOptions.push(expandedOption);
                    }
                  } else {
                    const expandedKey = option.label.replace('{index}', idx);
                    if (hasOptionValue(dynamicPrefillData[expandedKey])) {
                      expandedOption.label = dynamicPrefillData[expandedKey];
                      dynamicFieldOptions.push(expandedOption);
                    }
                  }
                });
              } else {
                const optiontObj = structuredClone(option);
                const optionArr = optiontObj.label;
                if (Array.isArray(optionArr)) {
                  const labelStrArr = [];
                  optionArr.forEach((dynamicFieldVal) => {
                    if (hasOptionValue(dynamicPrefillData[dynamicFieldVal])) {
                      labelStrArr.push(dynamicPrefillData[dynamicFieldVal]);
                    }
                  });
                  if (labelStrArr.length > 0) {
                    optiontObj.label = labelStrArr.join(', ');
                    dynamicFieldOptions.push(optiontObj);
                  }
                } else if (hasOptionValue(dynamicPrefillData[optionArr])) {
                  optiontObj.label = dynamicPrefillData[optionArr];
                  dynamicFieldOptions.push(optiontObj);
                }
              }
            });
          }
          setOptions(dynamicFieldOptions);
        } else if (dynamicEntityDataOptions) {
          const dynamicFieldOptions = [...fieldOptions];
          let index = dynamicFieldOptions.length;
          const uniqueValues = new Set();

          if (dynamicEntityDataOptions.tables) {
            // Track composite keys seen in this render pass to prevent duplicate
            // dropdown options when the same person appears in multiple tables.
            // Using a render-local Set (not expandableTableData) so options are
            // correctly rebuilt after entity deletion.
            const seenKeys = new Set();

            dynamicEntityDataOptions.tables.forEach((table) => {
              const requiredUnique = table.requiredUnique === true;
              const excludedValues = new Set(
                (table.excludeUsedValuesFrom || []).flatMap((excludeConfig) => {
                  const rows = entityData[excludeConfig.tableName] || [];
                  return rows.reduce((values, row) => {
                    if (
                      excludeConfig.ignoreCurrentEditCompositeKey
                      && entityToEdit?.compositeKey
                      && row?.compositeKey === entityToEdit.compositeKey
                    ) {
                      return values;
                    }

                    const excludedValue = row?.[excludeConfig.fieldKey];
                    if (hasOptionValue(excludedValue)) {
                      values.push(String(excludedValue));
                    }
                    return values;
                  }, []);
                })
              );

              if (entityData[table.tableName]) {
                entityData[table.tableName].forEach((data) => {
                  if (table.filterBy && data[table.filterBy.field] === table.filterBy.value) {
                    return;
                  }
                  const value = table.valueKeys.map((key) => data[key]);
                  const normalizedValue = (table.valuePrefix || '') + value.join(table?.valueJoin || ' ');

                  if (excludedValues.has(String(normalizedValue))) {
                    return;
                  }

                  if (requiredUnique && uniqueValues.has(normalizedValue)) {
                    return;
                  }

                  // If this composite key was already added from an earlier table
                  // in this render pass, skip the duplicate dropdown option but still
                  // allow expandableTableData to be rebuilt correctly on every render.
                  if (seenKeys.has(normalizedValue)) {
                    return;
                  }
                  seenKeys.add(normalizedValue);

                  const id = `${index}-${value.join('-')}`.toLowerCase();
                  const baseLabel = table.labelKeys.map((key) => data[key]).join(table?.labelJoin || ' ');
                  const appendStr = table?.labelAppendKey
                  // eslint-disable-next-line no-extra-parens -- desc
                    ? (data[table.labelAppendKey] ? ` - ${data[table.labelAppendKey]}` : '')
                    : table?.labelAppend || '';
                  dynamicFieldOptions.push({
                    id,
                    label: baseLabel + appendStr,
                    value: normalizedValue,
                  });

                  if (requiredUnique) {
                    uniqueValues.add(normalizedValue);
                  }

                  dataObject.expandableTableData[normalizedValue] = { ...data };
                  dataObject.setExpandableTableData(dataObject.expandableTableData);
                  index += 1;
                });
              }

              const appendOptions = Array.isArray(table.appendOptions)
                ? table.appendOptions
                : [];

              appendOptions.forEach((option) => {
                if (!option?.label) {
                  return;
                }

                const optionValue = option.value ?? option.label;
                dynamicFieldOptions.push(createOption(option.label, optionValue));
              });
            });
          }

          if (dynamicEntityDataOptions.optinsFromPropertyKey) {
            dynamicEntityDataOptions.optinsFromPropertyKey.forEach((propertyKey) => {
              const value = getVal(filledFormData, propertyKey);
              const id = `${index}-${value}`.toLowerCase();
              dynamicFieldOptions.push({
                id,
                label: value,
                value,
              });
              index += 1;
            });
          }

          if (Array.isArray(dynamicEntityDataOptions.prefillArrayOptions)) {
            dynamicEntityDataOptions.prefillArrayOptions.forEach((option) => {
              const prefillOptions = getPrefillArrayOptions(filledFormData, option);
              prefillOptions.forEach((prefillOption) => {
                dynamicFieldOptions.push(prefillOption);
              });
            });
          }

          setOptions(dynamicFieldOptions);
        } else if (fromStaticMetadataWithDynamicKey) {
          setOptions(
            formContainer.staticMetadata?.[fieldId]?.[
              getVal(filledFormData, fromStaticMetadataWithDynamicKey)
            ]
          );
        } else {
          setOptions(fieldOptions);
        }
      } else if (fieldOptionObject.type === 'static') {
        setOptions(fieldOptionObject.fieldOptions);
      } else if (fieldOptionObject.type === 'suppCards') {
        const optns = getVal(dataObject, 'memberInfo.supplementaryAccounts') || [];
        setOptions(optns);
      } else if (fieldOptionObject.type === 'generateOptionsWithLabelsAndValues') {
        const generatedOptions = [];
        // if there's any default option ->
        if (fieldOptionObject?.optionStructure?.defaultLabel) {
          generatedOptions.push(
            createOption(fieldOptionObject?.optionStructure?.defaultLabel,
              fieldOptionObject?.optionStructure?.defaultValue));
        }
        let optionsData = [];
        if (fieldOptionObject.optionsFrom === 'allUserCards') {
          // all cards from memberContext
          memberContextVal.forEach((card) => {
            optionsData = [...optionsData, card];
            if (card?.supplementaryAccounts?.length > 0) {
              optionsData = [...optionsData, ...card.supplementaryAccounts];
            }
          });
        } else {
          optionsData = getVal(extendedDataObject, fieldOptionObject.optionsFrom) || [];
        }

        // Filter items by a static field/value pair when configured.
        if (fieldOptionObject?.optionStructure?.filterBy) {
          const { field, value: filterValue } = fieldOptionObject.optionStructure.filterBy;
          optionsData = optionsData.filter(
            (item) => String(getVal(item, field)) === String(filterValue));
        }

        if (optionsData?.length > 0) {
          optionsData.forEach((optn) => {
            const label = combineValuesWithSepartors(
              getValuesFromDataObject(optn, fieldOptionObject?.optionStructure?.labelKeys),
              fieldOptionObject?.optionStructure?.labelSeparators
            );
            let value = '';
            if (fieldOptionObject?.optionStructure?.isValueSameAsLabel) {
              value = label;
            } else {
              value = combineValuesWithSepartors(
                getValuesFromDataObject(optn, fieldOptionObject?.optionStructure?.valueKeys),
                fieldOptionObject?.optionStructure?.valueSeparators
              );
            }
            generatedOptions.push(createOption(label, value));
          });
        }

        // Append static options at the end of generated options when configured.
        if (Array.isArray(fieldOptionObject?.optionStructure?.appendOptions)) {
          fieldOptionObject.optionStructure.appendOptions.forEach((option) => {
            if (!option?.label) return;
            const optionValue = option.value ?? option.label;
            generatedOptions.push(createOption(option.label, optionValue));
          });
        }

        setOptions(generatedOptions);
      } else if (fieldOptionObject.type === 'mapOptionsBasedOnDependentValue') {
        // Dynamically creating dropdown options with a dependent value
        const dependsOnValue = getVal(dataObject, fieldOptionObject.dependsOn[0]) || '';
        const filteredOptions = fieldOptionObject.fieldOptions.filter((fieldOption) => {
          const { dependentValue } = fieldOption;
          return !dependentValue || dependentValue === dependsOnValue;
        });
        setOptions(filteredOptions);
      }
    },
    effectDependencies
  );

  return options;
};

export default useDropDownOptions;
